import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Home, MessageSquare, CalendarDays, HeartHandshake } from "lucide-react";
import { VintageMegaphone } from "@/components/PeopleIcons";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Left-side tabs (indices 0,1) and right-side tabs (indices 2,3). The Voces
// Refugium FAB floats in the middle between them.
const leftTabs = [
  { path: "/app/feed", label: "Inicio", icon: Home },
  { path: "/app/salas", label: "Salas", icon: MessageSquare },
];
const rightTabs = [
  { path: "/app/sessions", label: "Sesiones", icon: HeartHandshake },
  { path: "/app/events", label: "Eventos", icon: CalendarDays },
];

export default function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadTotal, setUnreadTotal] = useState(0);

  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications/unread`, { withCredentials: true });
      setUnreadTotal(data.total || 0);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  useEffect(() => {
    fetchUnread();
  }, [location.pathname, fetchUnread]);

  const isActive = (path) => location.pathname.startsWith(path);
  const vocesActive = isActive("/app/voces");

  const renderTab = (tab) => {
    const active = isActive(tab.path);
    const Icon = tab.icon;
    const showBadge = tab.label === "Salas" && unreadTotal > 0;
    const highlightUnread = tab.label === "Salas" && unreadTotal > 0 && !active;
    const colorClass = active
      ? "text-[#6B9070]"
      : highlightUnread
        ? "text-[#E48D57]"
        : "text-[#5B3E2E]/50 hover:text-[#5B3E2E]/80";
    return (
      <button
        key={tab.path}
        data-testid={`nav-${tab.label.toLowerCase()}`}
        onClick={() => navigate(tab.path)}
        className={`relative flex flex-col items-center gap-1 flex-1 py-2 transition-all duration-300 ${colorClass}`}
      >
        <div className="relative">
          <Icon size={22} strokeWidth={active || highlightUnread ? 2 : 1.5} />
          {showBadge && (
            <span
              data-testid="unread-badge"
              className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] bg-[#E48D57] text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 shadow-[0_2px_6px_rgba(217,120,51,0.45)]"
            >
              {unreadTotal > 99 ? "99+" : unreadTotal}
            </span>
          )}
        </div>
        <span className="text-xs font-medium">{tab.label}</span>
      </button>
    );
  };

  return (
    <nav
      data-testid="bottom-nav"
      className="fixed bottom-0 left-0 right-0 z-50 drop-shadow-[0_-4px_12px_rgba(91,62,46,0.08)] pb-[env(safe-area-inset-bottom)] bg-[#FDFAF4]"
    >
      <div className="relative max-w-lg mx-auto">
        {/* Half-sphere notch — a semicircle that hugs the FAB with ~5px
            of margin on all sides. Flat edge sits exactly on the nav top. */}
        <div
          aria-hidden="true"
          className="absolute left-1/2 -translate-x-1/2 w-[70px] h-[35px] rounded-t-full bg-[#FDFAF4] pointer-events-none"
          style={{ top: "-35px" }}
        />

        {/* The tab row — solid cream background, no top border */}
        <div className="relative flex items-center h-16 px-2 bg-[#FDFAF4]">
          {leftTabs.map(renderTab)}
          {/* Spacer where the FAB floats */}
          <div className="w-20 flex-shrink-0" aria-hidden="true" />
          {rightTabs.map(renderTab)}
        </div>

        {/* The elevated Voces Refugium FAB — soft tinted red style.
            Flat center sits at the nav top line so upper half pokes above
            inside the dome, lower half sits over the nav. */}
        <button
          data-testid="nav-voces"
          aria-label="Voces Refugium"
          onClick={() => navigate("/app/voces")}
          className={`absolute left-1/2 -translate-x-1/2 w-[60px] h-[60px] rounded-full flex items-center justify-center transition-all duration-300 shadow-[0_6px_18px_rgba(196,74,58,0.22)] border border-[#C44A3A]/25 active:scale-95 ${
            vocesActive ? "ring-4 ring-[#C44A3A]/25" : ""
          }`}
          style={{
            top: "-30px",
            background: "linear-gradient(135deg, #C44A3A22, #FDFAF4 60%, #C44A3A0F)",
          }}
        >
          <VintageMegaphone
            size={28}
            strokeWidth={1.9}
            className="text-[#C44A3A]"
          />
        </button>
      </div>
    </nav>
  );
}
